import Header from "@/components/Header";
import Footer from "@/components/Footer";
import UrlForm from "@/components/UrlForm";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo-data";
import { Search, ShieldCheck, BarChart2, CheckCircle2, ArrowRight } from "lucide-react";

const STEPS_DETAIL = [
  {
    num: "01",
    icon: Search,
    title: "Vous collez une URL",
    desc: "Vous saisissez l'adresse du site à vérifier dans notre formulaire. Pas besoin de https:// — notre outil normalise automatiquement l'URL, retire les paramètres inutiles et extrait le domaine.",
    detail: ["Accepte les URL avec ou sans https://", "Normalisation automatique de l'adresse", "Vérification du format avant analyse", "Blocage des entrées invalides"],
  },
  {
    num: "02",
    icon: ShieldCheck,
    title: "Analyse automatique du site",
    desc: "Notre outil visite le site et analyse automatiquement une série de signaux. Cette étape prend généralement entre 3 et 15 secondes selon la disponibilité et la taille du site.",
    detail: ["Vérification du protocole HTTPS", "Analyse de la structure du domaine", "Recherche des pages légales (mentions légales, CGV, confidentialité)", "Détection des coordonnées de contact", "Analyse du contenu et des formulations", "Recherche d'informations sur la société"],
  },
  {
    num: "03",
    icon: BarChart2,
    title: "Calcul du score de confiance",
    desc: "Chaque signal détecté est pondéré selon son importance. Le score final de confiance (0-100) résume l'ensemble des signaux en un indicateur clair et immédiatement compréhensible.",
    detail: ["Score de confiance de 0 à 100 (0 = très suspect, 100 = très rassurant)", "Niveaux : Risque faible / Vigilance / Risque élevé", "Chaque signal contribue au score selon sa gravité", "Le moteur de scoring est entièrement basé sur des règles explicables"],
  },
  {
    num: "04",
    icon: CheckCircle2,
    title: "Résultat actionnable",
    desc: "Vous recevez un résultat pédagogique avec tous les signaux détaillés, des recommandations adaptées à votre situation, et des liens vers les ressources officielles françaises.",
    detail: ["Score global avec explication", "Liste des signaux de vigilance détectés", "Liste des éléments rassurants", "Recommandations personnalisées selon le score", "Ressources officielles (SignalConso, Service-Public, CNIL)"],
  },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title="Comment ça marche — Notre méthode d'analyse"
        description="Découvrez comment Fiable ou Pas analyse un site marchand : 12+ signaux automatiques, scoring transparent, résultats actionnables. Méthode 100% explicable."
        path="/comment-ca-marche"
        keywords="méthode analyse site, scoring fiabilité, signaux confiance e-commerce"
        jsonLd={breadcrumbJsonLd([
          { name: "Accueil", path: "/" },
          { name: "Comment ça marche", path: "/comment-ca-marche" },
        ])}
      />
      <Header />
      <main className="flex-1 py-12 bg-[#F9FAFB]">
        <div className="container-app max-w-4xl">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A]">Méthode</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-heading text-slate-900 tracking-tight mt-3 mb-4">
              Comment ça marche ?
            </h1>
            <p className="text-slate-600 text-base max-w-2xl mx-auto leading-relaxed">
              Notre moteur d'analyse est entièrement basé sur des règles explicables et transparentes.
              Voici exactement ce qui se passe lorsque vous soumettez une URL.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-8 mb-14">
            {STEPS_DETAIL.map(({ num, icon: Icon, title, desc, detail }) => (
              <div key={num} className="card-base p-6 sm:p-8" data-testid={`how-step-${num}`}>
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center shadow-md">
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-black text-[#1E3A8A] bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">{num}</span>
                      <h2 className="text-xl font-bold font-heading text-slate-900">{title}</h2>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">{desc}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {detail.map(d => (
                        <li key={d} className="flex items-start gap-2 text-xs text-slate-600">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Affiliate Banner 1 */}
          {/* BANNER 1 REMOVED */}

          {/* Limitations */}
          <div className="card-base p-8 mb-10">
            <h2 className="text-xl font-bold font-heading text-slate-800 mb-4">Limites de notre analyse</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              Notre outil est performant pour détecter les signaux les plus courants, mais il a des limites qu'il est important de connaître :
            </p>
            <ul className="space-y-3">
              {[
                "Les analyses sont automatisées et peuvent manquer certains éléments ou en signaler incorrectement.",
                "Certains sites légitimes peuvent manquer de mentions légales ou de CGV par inadvertance.",
                "Les sites avec une protection anti-robot peuvent ne pas être accessibles lors de l'analyse.",
                "Certaines vérifications dépendent de sources externes et peuvent être indisponibles temporairement ou incomplètes.",
                "Un score élevé ne garantit pas qu'un site est sûr à 100 %, et un score faible doit être interprété comme un signal de vigilance renforcée.",
              ].map(l => (
                <li key={l} className="flex items-start gap-3 text-sm text-slate-600">
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  {l}
                </li>
              ))}
            </ul>
          </div>

          {/* Google Ad Slot */}
          {/* AD REMOVED */}

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-10 text-sm text-slate-600">
            <p className="font-semibold text-slate-800 mb-2">Outil d'aide à la décision</p>
            <p>Fiable ou Pas est un outil informatif. Nos résultats ne constituent pas un avis juridique et ne remplacent pas une démarche officielle. En cas de doute fort, contactez les autorités compétentes.</p>
          </div>

          {/* Affiliate Banner 2 */}
          {/* BANNER 2 REMOVED */}

          <div className="text-center">
            <h3 className="font-bold text-slate-800 mb-4">Essayez maintenant</h3>
            <UrlForm />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
